import { useEffect, useState } from "react";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const ROLE_BADGE = {
  kepala_sekolah: "bg-blue-50 text-blue-700 hover:bg-blue-50",
  bendahara: "bg-amber-50 text-amber-700 hover:bg-amber-50",
  orang_tua: "bg-emerald-50 text-emerald-700 hover:bg-emerald-50",
};

export default function UsersPage() {
  const { user, schoolId } = useAuth();
  const isSuper = user.role === "super_admin";
  const [users, setUsers] = useState(null);
  const [schools, setSchools] = useState([]);
  const [students, setStudents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "bendahara", school_id: "", phone: "" });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [credTarget, setCredTarget] = useState(null);
  const [credForm, setCredForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [savingCred, setSavingCred] = useState(false);
  const [roleFilter, setRoleFilter] = useState("semua");

  const canEditCred = (u) =>
    (isSuper && u.role !== "super_admin") ||
    (!isSuper && ["kepala_sekolah", "orang_tua"].includes(u.role));

  const openCred = (u) => {
    setCredTarget(u);
    setCredForm({ name: u.name, email: u.nisn ? u.nisn : (u.email.includes("@ortu.smartbendahara") ? "" : u.email), password: "", phone: u.phone || "" });
  };

  const handleSaveCred = async (e) => {
    e.preventDefault();
    setSavingCred(true);
    try {
      const payload = {};
      if (credForm.name && credForm.name !== credTarget.name) payload.name = credForm.name;
      if (credForm.email) payload.email = credForm.email;
      if (credForm.phone !== (credTarget.phone || "")) payload.phone = credForm.phone;
      if (credForm.password) payload.password = credForm.password;
      if (Object.keys(payload).length === 0) {
        toast.error("Isi minimal satu perubahan");
        setSavingCred(false);
        return;
      }
      await api.put(`/users/${credTarget.id}/credentials`, payload);
      toast.success("Kredensial berhasil diubah");
      setCredTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingCred(false);
    }
  };

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => {
      setUsers([]);
      toast.error(apiError(e));
    });
    if (isSuper) api.get("/schools").then((r) => setSchools(r.data)).catch(() => {});
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
  };

  useEffect(load, [schoolId]);

  const openAdd = () => {
    setForm({ name: "", email: "", password: "", role: "bendahara", school_id: isSuper ? schoolId : "", phone: "" });
    setSelectedStudents([]);
    setDialogOpen(true);
  };

  const toggleStudent = (id) => {
    setSelectedStudents((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isSuper) {
        await api.post("/users/staff", form);
        toast.success("Akun staf berhasil dibuat");
        if (isSuper && form.school_id && form.school_id !== schoolId) {
          toast.info("Staf dibuat untuk sekolah lain — pindahkan pemilih sekolah untuk melihatnya.");
        }
      } else {
        await api.post("/users/parent", {
          name: form.name, email: form.email, password: form.password, student_ids: selectedStudents,
        });
        toast.success("Akun orang tua berhasil dibuat");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${deleting.id}`);
      toast.success("Pengguna dihapus");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const canCreate = isSuper || user.role === "bendahara";

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {isSuper ? "Pengguna" : "Akun Orang Tua"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {isSuper ? "Kelola akun staf sekolah dan orang tua." : "Akun orang tua dibuat otomatis saat siswa ditambahkan (login & sandi awal = NISN siswa). Gunakan form ini hanya untuk akun tambahan dengan email khusus."}
          </p>
        </div>
        {canCreate && (
          <Button onClick={openAdd} data-testid="add-user-button" className="gap-2 rounded-md bg-[#0060AF] hover:bg-[#004B87] active:scale-[0.98]">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> {isSuper ? "Tambah Staf" : "Tambah Orang Tua"}
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4 max-w-xs">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semua" data-testid="users-role-semua">Semua Peran</SelectItem>
              {isSuper && <SelectItem value="bendahara" data-testid="users-role-bendahara">Bendahara</SelectItem>}
              <SelectItem value="kepala_sekolah" data-testid="users-role-kepsek">Kepala Sekolah</SelectItem>
              <SelectItem value="orang_tua" data-testid="users-role-ortu">Orang Tua</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>Login ID</TableHead>
                <TableHead>Peran</TableHead>
                {isSuper ? <TableHead>Sekolah</TableHead> : <TableHead>Anak</TableHead>}
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users === null && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-slate-500">Memuat data...</TableCell></TableRow>
              )}
              {users !== null && users.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-slate-500">Belum ada pengguna.</TableCell></TableRow>
              )}
              {(users || []).filter((u) => roleFilter === "semua" || u.role === roleFilter).map((u) => (
                <TableRow key={u.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <TableCell className="text-sm font-medium text-slate-800">{u.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {u.nisn ? <span data-testid={`user-loginid-${u.id}`}>NISN {u.nisn}</span> : u.email}
                  </TableCell>
                  <TableCell>
                    <Badge className={`rounded-full ${ROLE_BADGE[u.role] || ""}`}>{ROLE_LABELS[u.role]}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {isSuper
                      ? u.school_name
                      : (u.children || []).length > 0
                        ? u.children.map((c) => `${c.name}${c.class_name ? ` (${c.class_name})` : ""}`).join(", ")
                        : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canEditCred(u) && (
                        <Button variant="ghost" size="icon" onClick={() => openCred(u)} data-testid={`cred-user-${u.id}`} title="Ubah kredensial">
                          <KeyRound className="h-4 w-4 text-[#0060AF]" strokeWidth={1.5} />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => setDeleting(u)} data-testid={`delete-user-${u.email}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined} data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{isSuper ? "Tambah Akun Staf" : "Tambah Akun Orang Tua"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="u-name">Nama Lengkap</Label>
              <Input id="u-name" data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="u-email">Email</Label>
              <Input id="u-email" type="email" data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="u-password">Kata Sandi</Label>
              <Input id="u-password" type="text" data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} placeholder="Minimal 6 karakter" />
            </div>
            {isSuper && form.role === "bendahara" && (
              <div className="space-y-2">
                <Label htmlFor="u-phone">No WA Bendahara (untuk pengingat langganan)</Label>
                <Input id="u-phone" data-testid="user-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Contoh: 081234567890" />
              </div>
            )}
            {isSuper && (
              <>
                <div className="space-y-2">
                  <Label>Peran</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kepala_sekolah" data-testid="role-option-kepala">Kepala Sekolah</SelectItem>
                      <SelectItem value="bendahara" data-testid="role-option-bendahara">Bendahara</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sekolah</Label>
                  <Select value={form.school_id} onValueChange={(v) => setForm({ ...form, school_id: v })}>
                    <SelectTrigger data-testid="user-school-select"><SelectValue placeholder="Pilih sekolah..." /></SelectTrigger>
                    <SelectContent>
                      {schools.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`user-school-option-${s.id}`}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {!isSuper && (
              <div className="space-y-2">
                <Label>Tautkan ke Anak (Siswa)</Label>
                <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-3" data-testid="parent-student-list">
                  {students.length === 0 && <p className="text-sm text-slate-500">Belum ada siswa. Tambahkan data siswa dulu.</p>}
                  {students.map((s) => (
                    <label key={s.id} className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-slate-50">
                      <Checkbox
                        checked={selectedStudents.includes(s.id)}
                        onCheckedChange={() => toggleStudent(s.id)}
                        data-testid={`parent-student-check-${s.nisn}`}
                      />
                      <span className="text-sm text-slate-700">{s.name} <span className="text-xs text-slate-400">({s.class_name} · {s.nisn})</span></span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="user-form-cancel">Batal</Button>
              <Button type="submit" disabled={saving} data-testid="user-form-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                {saving ? "Menyimpan..." : "Buat Akun"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!credTarget} onOpenChange={() => setCredTarget(null)}>
        <DialogContent aria-describedby={undefined} data-testid="cred-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Ubah Kredensial — {credTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
            Kata sandi lama tidak bisa dilihat, hanya bisa direset. Kosongkan kolom yang tidak ingin diubah.
            {credTarget?.nisn && ` Akun ini login dengan NISN ${credTarget.nisn}.`}
          </div>
          {credTarget && (
            <form onSubmit={handleSaveCred} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cred-name">Nama</Label>
                <Input id="cred-name" data-testid="cred-name-input" value={credForm.name} onChange={(e) => setCredForm({ ...credForm, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cred-email">{credTarget?.nisn ? "Login ID — NISN (atau email)" : "Email Baru (opsional)"}</Label>
                <Input id="cred-email" type="text" data-testid="cred-email-input" value={credForm.email} onChange={(e) => setCredForm({ ...credForm, email: e.target.value })} placeholder="Biarkan kosong jika tidak diubah" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cred-password">Kata Sandi Baru (opsional)</Label>
                <Input id="cred-password" type="text" data-testid="cred-password-input" value={credForm.password} onChange={(e) => setCredForm({ ...credForm, password: e.target.value })} placeholder="Minimal 6 karakter" />
              </div>
              {credTarget?.role !== "orang_tua" && (
                <div className="space-y-2">
                  <Label htmlFor="cred-phone">No WA (untuk pengingat langganan)</Label>
                  <Input id="cred-phone" data-testid="cred-phone-input" value={credForm.phone} onChange={(e) => setCredForm({ ...credForm, phone: e.target.value })} placeholder="Contoh: 081234567890" />
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCredTarget(null)} data-testid="cred-cancel">Batal</Button>
                <Button type="submit" disabled={savingCred} data-testid="cred-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                  {savingCred ? "Menyimpan..." : "Simpan"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Hapus Pengguna"
        description={`Yakin ingin menghapus akun ${deleting?.name} (${deleting?.email})?`}
        onConfirm={handleDelete}
        testId="confirm-delete-user"
      />
    </div>
  );
}
